import { useLocation } from '../../../features/locations/use-location'
import { useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { Bar, btnCls, Crumbs, Dot, KV, linkCls, mono, Note, Panel, SkelPanels, SLink, Stat } from './ui'

export function SignalLocationDetail() {
  const idOrCode = useIdOrCode()
  const { data: loc, isLoading, error } = useLocation(idOrCode)
  const net = useNetworkOverview(idOrCode)
  const base = `/locations/${encodeURIComponent(idOrCode)}`

  if (isLoading)
    return (
      <div className="h-full overflow-auto">
        <SkelPanels />
      </div>
    )
  if (error) return <Note error>Failed to load location: {error.message}</Note>
  if (!loc) return <Note>No location matches "{idOrCode}".</Note>

  const profile = loc.networkProfiles?.[0] // Drizzle relations are arrays
  const code = loc.aptosStoreCode ?? loc.code
  const address = [loc.addressLine1, [loc.addressCity, loc.addressState].filter(Boolean).join(', '), loc.addressZipCode]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={<Crumbs idOrCode={idOrCode} trail="detail" />}
        title={
          <span className="inline-flex items-center gap-2">
            <span style={mono}>{code}</span>
            <span className="font-normal text-[var(--color-muted)]">{loc.name}</span>
          </span>
        }
        meta={
          <span className="inline-flex items-center gap-1.5 text-[12px]">
            <Dot tone={loc.status ? 'ok' : 'off'} />
            {loc.status ? 'Active' : 'Inactive'}
          </span>
        }
        right={
          <>
            <SLink to={`${base}/network`} className={btnCls}>
              Network
            </SLink>
            <SLink to={`${base}/network/wan-settings`} className={btnCls}>
              WAN settings
            </SLink>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <p className="mb-3 text-[12px] text-[var(--color-muted)]" style={mono}>
          {[loc.banner?.toUpperCase(), loc.region, loc.district].filter(Boolean).join(' / ') || '-'}
          {address ? ` · ${address}` : ''}
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <Panel title="Facts">
            <KV
              items={[
                ['Banner', loc.banner?.toUpperCase()],
                ['Region', loc.region],
                ['District', loc.district],
                ['Type', loc.locationTypeLabel],
                ['Store code', code],
                ['Terminals', loc.terminals ? String(loc.terminals.length) : null],
              ]}
            />
          </Panel>
          <Panel title="Network profile">
            {profile ? (
              <KV
                items={[
                  ['Lifecycle', profile.lifecycleState],
                  ['Edge', profile.edgeVendor],
                  ['Switching', profile.switchVendor],
                  ['Cloud gateway', profile.cgVendor],
                  ['Primary GW', profile.ipPrimaryGw ? String(profile.ipPrimaryGw) : null],
                  ['Primary CIDR', profile.ipPrimaryCidr ? String(profile.ipPrimaryCidr) : null],
                ]}
              />
            ) : (
              <Note>No network profile for this store.</Note>
            )}
          </Panel>
        </div>
        <div className="mt-3">
          <Panel
            title="Device status"
            aside={
              <SLink to={`${base}/network`} className={linkCls}>
                open network
              </SLink>
            }
          >
            {net.isLoading ? (
              <div className="grid gap-2 p-3" aria-busy="true">
                <span className="block h-2 w-1/3 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
                <span className="block h-2 w-2/3 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
              </div>
            ) : net.data ? (
              <div className="grid grid-cols-2 md:grid-cols-4">
                <Stat label="Devices" value={net.data.summary.total} />
                <Stat label="Up" value={net.data.summary.up} tone="ok" />
                <Stat label="Down" value={net.data.summary.down} tone={net.data.summary.down > 0 ? 'bad' : undefined} />
                <Stat label="Other" value={net.data.summary.other} />
              </div>
            ) : (
              <Note error={!!net.error}>{net.error ? 'Device status unavailable.' : 'No device status reported.'}</Note>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
